// Form QC35 (Revised) — File Archival Form checklist (May 2026 revision).
//
// The checklist IS the QC35 (there is no document to upload; the approved
// form prints from these answers). Rows the system already knows are shown
// read-only by components/workflow/Qc35Checklist.js (Client Name / Reporting
// Year-end come from the covered engagement); everything else on the form is
// typed by the submitter, including the CaseWare lock-down confirmation (free
// text). The firm name defaults to PKF-CAP LLP and stays editable.
//
// `derived` rows are the exception INSIDE the checklist table: the value is
// read live off the covered engagement and is never an answer. Date of Audit
// Report is the Signed FS record's signing date — the firm signs the report
// once, and that date already lives on the FS record (engagements have no
// sign_off_date column; it is a JOIN). Typing it again here would be a second
// copy that a later FS Amendment could not correct, so the row prints the
// live value everywhere: form, reviewer's view, Management record and PDF.
//
// The three time-involvement rows carry the QMM guideline (the table printed
// under the checklist) and a Comments box each:
//   EP hours over total          High risk 5%, Moderate / Low risk 3%
//   EP + Manager / Director      15% whatever the rating
//   EQR hours                    High risk at least 13 hours; Moderate risk
//                                depends on the engagement's complexity (no
//                                fixed minimum); Low risk not applicable
// The Comments box becomes required the moment the value falls below the
// guideline — an explanation is expected, not optional. The EQR row itself is
// required when the QC6 / QC7 form (or its addendum) names a reviewer, and is
// locked "Not applicable" when it names none. Both rules need the covered
// engagement's QC context — `ctx = { riskRating, eqrAssigned }`, served by
// GET /api/workflow/qc35/qc-context/:engagementId and computed the same way
// on the server (services/qc35Context.js). Without a context (a legacy
// engagement with no QC record) the lower guideline applies and the EQR row
// stays optional — the form never claims a breach it cannot prove.
//
// The fee-recovery explanation row exists only while the rate is below 30%
// (`showWhen`; a hidden row's value is cleared and never required).
//
// Editing/read surfaces share this bank: WorkflowQc35New, WorkflowQc35Detail
// (view + Modify modal), WorkflowQc35aNew / WorkflowQc35aDetail and the
// Management QC35 Form record form (recordKinds). The server validates
// submissions against the same bank, and server/services/qc35FormPdf.js
// prints it (CommonJS on purpose — same discipline as qcQuestions.js).
//
// Item shape (subset of qcQuestions'):
//   { id, label, type, tip?, required?, header?, showWhen?, derived?,
//     guideline?, commentFor?, na? }
//   type: 'text' | 'date' | 'percent' | 'hours' | 'textarea'
//     'hours' is the EQR row's unit — the two partner/manager rows are a
//     share of total engagement hours, the EQR's is counted in hours (the
//     QMM guideline is written in hours).
//   required: boolean | (answers, ctx) => boolean
//   header: rendered in the form header, not the checklist table
//   showWhen: (answers) => boolean — the row exists only while true; a hidden
//     row's value is cleared by applyQc35AutoAnswers and never required.
//   derived: name of the covered engagement's field holding the value — the
//     row is read-only on every surface, never required, never an answer
//     (applyQc35AutoAnswers drops any stale value under its id) and never
//     a field-comment target.
//   guideline: 'ep' | 'epm' | 'eqr' — which QMM guideline the row is measured
//     against (qc35GuidelineOf prints the applicable threshold).
//   commentFor: id of the row this Comments box belongs to — rendered inside
//     that row's Response cell, printed as its own row on the PDF, keyed as
//     its own answer for History diffs and reviewer comments.
//   na: { when: (answers, ctx) => boolean, value } — while `when` holds the
//     row is locked to `value` (its Comments box is dropped); an unknown
//     context (null) leaves the row as it is.

const { isSentinelName } = require('../utils/staffSentinels');

const DEFAULT_FIRM_NAME = 'PKF-CAP LLP';

const blank = (v) => v === undefined || v === null || String(v).trim() === '';
const num = (v) => (blank(v) || !Number.isFinite(Number(v)) ? null : Number(v));
const recoveryLow = (v) => num(v) !== null && num(v) < 30;

// ── QMM time-involvement guideline ──────────────────────────────────────────
const RISK_HIGH = 'High';
const RISK_MODERATE = 'Moderate';
const RISK_LOW = 'Low';

const QC35_TIME_GUIDELINE = {
  epHigh: 5,      // % of total engagement hours, High risk
  epOther: 3,     // % of total engagement hours, Moderate / Low risk
  epm: 15,        // % of total engagement hours, every rating
  eqrHigh: 13,    // hours, High risk
};

const ratingOf = (ctx) => (ctx && ctx.riskRating ? String(ctx.riskRating) : null);
/** true / false when the QC context says so, null when there is no context. */
const eqrAssignedOf = (ctx) => (ctx && typeof ctx.eqrAssigned === 'boolean' ? ctx.eqrAssigned : null);

const epMin = (ctx) => (ratingOf(ctx) === RISK_HIGH ? QC35_TIME_GUIDELINE.epHigh : QC35_TIME_GUIDELINE.epOther);
const eqrMin = (ctx) => (ratingOf(ctx) === RISK_HIGH ? QC35_TIME_GUIDELINE.eqrHigh : null);
const below = (v, min) => min !== null && num(v) !== null && num(v) < min;

/**
 * The guideline sentence printed under a time-involvement row for this
 * engagement — the threshold that applies given the QC form's risk rating,
 * or the whole rule when the rating is not on record.
 */
const qc35GuidelineOf = (item, ctx) => {
  if (!item || !item.guideline) return null;
  const rating = ratingOf(ctx);
  const g = QC35_TIME_GUIDELINE;
  switch (item.guideline) {
    case 'ep':
      if (rating === RISK_HIGH) return `Guideline: at least ${g.epHigh}% of total engagement hours (High risk).`;
      if (rating === RISK_MODERATE || rating === RISK_LOW) return `Guideline: at least ${g.epOther}% of total engagement hours (${rating} risk).`;
      return `Guideline: at least ${g.epHigh}% of total engagement hours for High risk, ${g.epOther}% for Moderate / Low risk. The risk rating is not on record — ${g.epOther}% applies.`;
    case 'epm':
      return `Guideline: at least ${g.epm}% of total engagement hours.`;
    case 'eqr':
      if (rating === RISK_HIGH) return `Guideline: at least ${g.eqrHigh} hours (High risk).`;
      if (rating === RISK_MODERATE) return 'Guideline: subject to the complexity of the engagement — no fixed minimum (Moderate risk).';
      if (rating === RISK_LOW) return 'Guideline: not applicable (Low risk).';
      return `Guideline: at least ${g.eqrHigh} hours for High risk; Moderate risk depends on the engagement's complexity; not applicable for Low risk. The risk rating is not on record.`;
    default:
      return null;
  }
};

/**
 * Whether the guideline is NOT met for a time-involvement row — the trigger
 * that makes its Comments box required. Nothing to measure against (no
 * value, or no fixed minimum for this rating) ⇒ false.
 */
const qc35GuidelineNotMet = (item, answers, ctx) => {
  const a = answers || {};
  if (!item || !item.guideline) return false;
  switch (item.guideline) {
    case 'ep': return below(a.q35_ep_time, epMin(ctx));
    case 'epm': return below(a.q35_epm_time, QC35_TIME_GUIDELINE.epm);
    case 'eqr': return below(a.q35_eqr_time, eqrMin(ctx));
    default: return false;
  }
};

const TIP_TIME_GUIDELINE = 'Where the guideline is not met, an explanation is required in the Comments box. The guideline extracted from the QMM is printed under the table.';
const NA_NO_EQR = 'Not applicable — no engagement quality reviewer';

const comment = (parentId, parentLabel) => ({
  id: `${parentId}_comment`,
  label: `${parentLabel} — Comments`,
  type: 'textarea',
  commentFor: parentId,
});

const LABEL_EP = 'Percentage of time involvement by the Engagement Partner';
const LABEL_EPM = 'Percentage of time involvement by the Engagement Partner and Manager / Director';
const LABEL_EQR = 'Time involvement by the Engagement Quality Reviewer, if any';

const QC35_ITEMS = [
  {
    id: 'q35_firm_name',
    label: "Audit Firm's Name",
    type: 'text', required: true, header: true,
  },
  {
    id: 'q35_audit_report_date',
    label: 'Date of Audit Report',
    type: 'date', derived: 'signOffDate',
    tip: 'Taken from the approved Signed Financial Statements — it is the report signing date on record, so it is not typed again here.',
  },
  {
    id: 'q35_lockdown',
    label: 'Date of approval and confirmation that the CaseWare Audit files have been locked down within 50 days from the date of the Audit Report',
    type: 'textarea', required: true,
    tip: 'Refer to the CaseWare Engagement History screenshot in the uploaded QC35 Form (File Locked Down date, engagement name and financial year-end).',
  },
  {
    id: 'q35_ep_time',
    label: LABEL_EP,
    type: 'percent', required: true, tip: TIP_TIME_GUIDELINE, guideline: 'ep',
  },
  Object.assign(comment('q35_ep_time', LABEL_EP), {
    required: (a, ctx) => qc35GuidelineNotMet({ guideline: 'ep' }, a, ctx),
  }),
  {
    id: 'q35_epm_time',
    label: LABEL_EPM,
    type: 'percent', required: true, tip: TIP_TIME_GUIDELINE, guideline: 'epm',
  },
  Object.assign(comment('q35_epm_time', LABEL_EPM), {
    required: (a, ctx) => qc35GuidelineNotMet({ guideline: 'epm' }, a, ctx),
  }),
  {
    id: 'q35_eqr_time',
    label: LABEL_EQR,
    type: 'hours', guideline: 'eqr',
    tip: [TIP_TIME_GUIDELINE, 'Required when the QC6 / QC7 form (or its addendum) names an engagement quality reviewer; locked "Not applicable" when it names none.'],
    required: (a, ctx) => eqrAssignedOf(ctx) === true,
    na: { when: (a, ctx) => eqrAssignedOf(ctx) === false, value: NA_NO_EQR },
  },
  Object.assign(comment('q35_eqr_time', LABEL_EQR), {
    required: (a, ctx) => qc35GuidelineNotMet({ guideline: 'eqr' }, a, ctx),
  }),
  {
    id: 'q35_recovery',
    label: 'Actual Fee Recovery % (Fee / Time Cost)',
    type: 'percent', required: true,
    tip: 'Should be at least 30%. Where the guideline of 30% is not met, please provide an explanation.',
  },
  {
    id: 'q35_recovery_explain',
    label: 'Fee recovery — explanation (recovery rate below 30%)',
    type: 'textarea', required: true,
    showWhen: (a) => recoveryLow(a.q35_recovery),
  },
];

const byId = Object.fromEntries(QC35_ITEMS.map((q) => [q.id, q]));

const getQc35Items = () => QC35_ITEMS;

/** Whether an item's row exists for these answers. */
const isQc35ItemShown = (item, answers = {}) => (item.showWhen ? item.showWhen(answers) : true);

/** System-filled row (value off the covered engagement, not off `answers`). */
const isQc35DerivedItem = (item) => !!(item && item.derived);

/** The Comments box that belongs to a row, if it has one. */
const qc35CommentItemOf = (item) => QC35_ITEMS.find((q) => q.commentFor === item.id) || null;

/**
 * The item's N/A state for this context: true = locked to its `na.value`,
 * false = answered normally, null = unknown (no context — leave the row as
 * the submitter left it). A Comments box follows its parent row.
 */
const qc35NaStateOf = (item, answers, ctx) => {
  if (!item) return false;
  if (item.commentFor) return qc35NaStateOf(byId[item.commentFor], answers, ctx);
  if (!item.na) return false;
  if (!ctx) return null;
  return !!item.na.when(answers || {}, ctx);
};

/** Locked on every surface (the value is the bank's, not the submitter's). */
const isQc35ItemLocked = (item, answers, ctx) => qc35NaStateOf(item, answers, ctx) === true;

/**
 * A derived row's value, read off the covered engagement (the primary one —
 * a QC35 submission covers a single company). Undefined when the engagement
 * has nothing to offer yet, which the surfaces print as an em dash.
 */
const qc35DerivedValue = (item, engagement) => (
  isQc35DerivedItem(item) && engagement ? engagement[item.derived] : undefined
);

/**
 * `answers.<id>` → wording (History diffs / reviewer comments). Derived rows
 * are out: nobody answered them, so there is nothing to comment on or diff.
 */
const qc35AnswerLabels = () => Object.fromEntries(
  QC35_ITEMS.filter((q) => !isQc35DerivedItem(q)).map((q) => [`answers.${q.id}`, q.label])
);

/**
 * Keep the answers in step with the rows that exist: the firm name defaults
 * to PKF-CAP LLP the first time the form renders (an untouched key only —
 * a cleared field stays cleared so it can be retyped), and a conditional
 * row's value is dropped the moment its condition stops holding. A derived
 * row is dropped outright — the value lives on the engagement, so a copy
 * under its id (an older submission's answer, a client posting one) would be
 * a second truth nobody maintains. With a QC context, an N/A row is pinned
 * to its placeholder (and its Comments box dropped); when the context says
 * the row applies again, a stale placeholder is cleared so the row is asked.
 */
const applyQc35AutoAnswers = (answers, ctx = null) => {
  let a = Object.assign({}, answers || {});
  if (a.q35_firm_name === undefined) {
    a = Object.assign({}, a, { q35_firm_name: DEFAULT_FIRM_NAME });
  }
  const drop = (id) => {
    if (a[id] === undefined) return;
    const next = Object.assign({}, a);
    delete next[id];
    a = next;
  };
  QC35_ITEMS.forEach((item) => {
    if (isQc35DerivedItem(item) || !isQc35ItemShown(item, a)) { drop(item.id); return; }
    const na = qc35NaStateOf(item, a, ctx);
    if (item.commentFor) {
      if (na === true) drop(item.id);
      return;
    }
    if (na === true && a[item.id] !== item.na.value) a = Object.assign({}, a, { [item.id]: item.na.value });
    if (na === false && item.na && a[item.id] === item.na.value) drop(item.id);
  });
  return a;
};

/**
 * Two answer sets, same content?
 *
 * 不能直接比 JSON 字串：從 Postgres 讀回來的 JSONB 鍵序是它自己排的，而
 * `applyQc35AutoAnswers` 又會重建物件——同一份表比出來會「不一樣」，
 * 修改單就會以為使用者改了東西。所以比之前先正規化：鍵排序、空值視同沒填。
 */
const sameQc35Answers = (a, b) => {
  // 衍生 id 也不算：舊記錄裡可能還躺著一份當年抄下來的值，新的一份不再帶它
  // ——那不是「使用者改了東西」，修改單不該因此以為表被動過。
  const derivedIds = new Set(QC35_ITEMS.filter(isQc35DerivedItem).map((q) => q.id));
  const norm = (o) => {
    const src = o || {};
    const out = {};
    Object.keys(src).sort().forEach((k) => {
      if (!blank(src[k]) && !derivedIds.has(k)) out[k] = src[k];
    });
    return JSON.stringify(out);
  };
  return norm(a) === norm(b);
};

/** Required for these answers in this context (false for a locked row). */
const isQc35ItemRequired = (item, answers, ctx) => {
  if (!item || isQc35DerivedItem(item) || isQc35ItemLocked(item, answers, ctx)) return false;
  return typeof item.required === 'function' ? !!item.required(answers || {}, ctx) : !!item.required;
};

// A percentage is a share of total engagement hours (or fee over cost): it
// lives in 0–100. Anything else is a typo, not an answer.
const PERCENT_MAX = 100;
const percentOutOfRange = (item, v) => (
  item.type === 'percent' && !blank(v) && (num(v) === null || num(v) < 0 || num(v) > PERCENT_MAX)
);
/** The inline message under a percentage typed outside 0–100 (null when fine). */
const qc35PercentError = (item, v) => (percentOutOfRange(item, v) ? `Must be between 0 and ${PERCENT_MAX}.` : null);

/**
 * Checklist fields that stop a submission → [{ key, label }]: required rows
 * still unanswered, and percentages outside 0–100 (label suffixed so the
 * banner says which it is). key = `answers.<id>` so formValidation marks the
 * very rows concerned. Hidden and locked rows are never required.
 */
const qc35AnswerGaps = (answers, ctx = null) => {
  const a = answers || {};
  const gaps = [];
  QC35_ITEMS.forEach((item) => {
    if (isQc35DerivedItem(item) || !isQc35ItemShown(item, a) || isQc35ItemLocked(item, a, ctx)) return;
    if (blank(a[item.id])) {
      if (isQc35ItemRequired(item, a, ctx)) gaps.push({ key: `answers.${item.id}`, label: item.label });
    } else if (percentOutOfRange(item, a[item.id])) {
      gaps.push({ key: `answers.${item.id}`, label: `${item.label} (must be between 0 and ${PERCENT_MAX})` });
    }
  });
  return gaps;
};

/** The same check as a list of labels — for banners / server messages. */
const missingQc35Answers = (answers, ctx = null) => qc35AnswerGaps(answers, ctx).map((g) => g.label);

/** One read-only rendering of an answer value. */
const fmtQc35AnswerValue = (item, v) => {
  if (blank(v)) return '—';
  if (item && item.type === 'percent') return `${v} %`;
  // An N/A placeholder sits in the hours row as text — print it as it is.
  if (item && item.type === 'hours') return num(v) === null ? String(v) : `${v} ${num(v) === 1 ? 'hour' : 'hours'}`;
  return String(v);
};

/**
 * One line describing the QC context the checklist was measured against —
 * printed with the guideline so the submitter and the reviewer see the same
 * rating and EQR the rules used.
 */
const qc35ContextSummary = (ctx) => {
  if (!ctx) return 'Risk rating and engagement quality reviewer: not on record (no approved QC6 / QC7 form) — the lower guideline applies and the EQR row is optional.';
  const rating = ratingOf(ctx) || 'not on record';
  const eqr = eqrAssignedOf(ctx) === true
    ? `engagement quality reviewer ${ctx.eqr ? `— ${ctx.eqr}` : 'assigned'}`
    : eqrAssignedOf(ctx) === false ? 'no engagement quality reviewer' : 'engagement quality reviewer not on record';
  return `From the QC6 / QC7 form${ctx.revisedBy ? ` and its addendum ${ctx.revisedBy}` : ''}: risk rating ${rating}; ${eqr}.`;
};

/**
 * Replace an object-valued `answers` change with one row per checklist field
 * (History diffs; the repository stores whole before/after answer objects).
 * Ids no longer in the bank still appear under their raw id, so a historical
 * answer never silently vanishes — except a derived id, whose stale copy is
 * dropped by applyQc35AutoAnswers on the next write: printing that as a
 * change would tell the reader someone deleted an answer, which nobody did.
 */
const expandQc35AnswerChanges = (changes) => {
  const answers = changes && changes.answers;
  if (!answers || typeof answers !== 'object' || !('before' in answers || 'after' in answers)) {
    return changes || {};
  }
  const rest = Object.assign({}, changes);
  delete rest.answers;
  const before = answers.before || {};
  const after = answers.after || {};
  const known = new Set(QC35_ITEMS.map((q) => q.id));
  const extras = [...new Set([...Object.keys(before), ...Object.keys(after)])]
    .filter((id) => !known.has(id))
    .map((id) => ({ id, label: id, type: 'text' }));
  const rows = {};
  [...QC35_ITEMS.filter((q) => !isQc35DerivedItem(q)), ...extras].forEach((q) => {
    const b = fmtQc35AnswerValue(q, before[q.id]);
    const a = fmtQc35AnswerValue(q, after[q.id]);
    if (b === a) return;
    rows[`answers.${q.id}`] = { before: b, after: a, label: q.label };
  });
  return Object.assign({}, rest, rows);
};

/**
 * Build the QC context from what the server knows about the covered
 * engagement's QC6 / QC7 record — `riskRating` and `eqr` are the record's
 * CURRENT values (its latest addendum wins; qcAddendaView.currentOf). A
 * sentinel EQR ("NOT REQUIRED", "TO BE DETERMINED") names nobody.
 */
const qc35ContextOf = ({ riskRating = null, eqr = null, revisedBy = null, qcRecordId = null } = {}) => ({
  qcRecordId: qcRecordId || null,
  riskRating: riskRating || null,
  eqr: blank(eqr) || isSentinelName(eqr) ? null : String(eqr).trim(),
  eqrAssigned: !(blank(eqr) || isSentinelName(eqr)),
  revisedBy: revisedBy || null,
});

// CommonJS on purpose: the server validates submissions from this same bank
// (server/routes/workflow.routes.js, workflow.amendments.routes.js,
// engagementRecords.routes.js) and prints it (server/services/qc35FormPdf.js).
module.exports = {
  QC35_ITEMS,
  QC35_TIME_GUIDELINE,
  DEFAULT_FIRM_NAME,
  NA_NO_EQR,
  getQc35Items,
  isQc35ItemShown,
  isQc35DerivedItem,
  isQc35ItemLocked,
  isQc35ItemRequired,
  qc35CommentItemOf,
  qc35DerivedValue,
  qc35GuidelineOf,
  qc35GuidelineNotMet,
  qc35AnswerLabels,
  qc35ContextOf,
  qc35ContextSummary,
  applyQc35AutoAnswers,
  sameQc35Answers,
  qc35AnswerGaps,
  missingQc35Answers,
  qc35PercentError,
  fmtQc35AnswerValue,
  expandQc35AnswerChanges,
};
